// components/Trigger.tsx
import React from 'react';
import { Button, Pagination, Spin } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import SearchInput from '../../../components/common/SearchInput';
import NoData from '../../../components/common/NoData';
import moment from 'moment';
import { Trigger as TriggerType } from '../../../hooks/api/notification/notificationService';


interface TriggerProps {
  triggers: TriggerType[];
  loading: boolean;
  total: number;
  currentPage: number;
  pageSize: number;
  onEdit: (trigger: TriggerType) => void;
  onDelete: (triggerId: string) => void;
  onSearch: (searchTerm: string) => void;
  onPageChange: (page: number, pageSize: number) => void;
}

const Trigger: React.FC<TriggerProps> = ({
  triggers,
  loading,
  total,
  currentPage,
  pageSize,
  onEdit,
  onDelete,
  onSearch,
  onPageChange
}) => {
  const handleSearch = (searchTerm: string) => {
    onSearch(searchTerm);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    onPageChange(page, pageSize);
  };

  const formatActions = (actions: any[]) => {
    return actions.map(action => {
      if (action.type === 'send_sms' ) {
        // || action.type === 'send_whatsapp'
        return `Send ${action.channel?.toUpperCase()}`;
      } else if (action.type === 'add_bonus') {
        return `Add ${action.count} ${action.bonus_type}`;
      }
      return action.type.replace('_', ' ');
    }).join(' + ');
  };

  const formatLastRun = (lastRun: string | null) => {
    if (!lastRun) return 'Never';
    return moment(lastRun).format('DD/MM/YYYY HH:mm');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="h-container">
        <div className="flex flex-wrap lg:flex-nowrap gap-x-72 items-center justify-between mb-4">
          <div className="mb-2 w-full">
            <h3 className="chart-heading">Notification Triggers</h3>
            <p className="chart-para">
              Manage automation triggers for SMS/WhatsApp notifications
            </p>
          </div>
          <div className="flex items-center gap-x-3">
            <div className="lg:w-[340px]">
              <SearchInput
                setSearchTerm={handleSearch}
                setPageNumber={() => {}} // Handled internally
                placeholder="Search by trigger name, criteria, action"
              />
            </div>
          </div>
        </div>

        <div className="mt-4">
          {triggers.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {triggers.map((trigger) => (
                  <div
                    key={trigger.id}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-4"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between pb-3 border-b border-gray-100">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {trigger.name}
                          </h3>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              trigger.is_active
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-gray-100 text-gray-800 border border-gray-200'
                            }`}
                          >
                            {trigger.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mt-1">
                          {trigger.description}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          Type: {trigger.trigger_type.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => onEdit(trigger)}
                          className="text-gray-600 hover:text-blue-600"
                        />
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => onDelete(trigger.id)}
                          className="text-gray-600 hover:text-red-600"
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="pt-3">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs mb-1">Action</p>
                          <p className="text-gray-900 font-medium text-xs">
                            {formatActions(trigger.actions)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs mb-1">Last Run</p>
                          <p className="text-gray-900 font-medium text-xs">
                            {formatLastRun(trigger.last_run)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs mb-1">Users Matched</p>
                          <p className="text-gray-900 font-medium text-xs">
                            {trigger.users_matched}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-gray-500 text-xs">
                          Schedule: <span className="text-gray-700 font-medium">
                            {trigger.schedule_date && trigger.schedule_time 
                              ? `${new Date(trigger.schedule_date).toLocaleDateString()} at ${trigger.schedule_time}`
                              : trigger.schedule_time 
                              ? `Daily at ${trigger.schedule_time}`
                              : 'Not scheduled'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {total > pageSize && (
                <div className="flex mt-6 justify-end">
                  <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={total}
                    onChange={handlePageChange}
                    showSizeChanger
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`
                    }
                  />
                </div>
              )}
            </>
          ) : (
            <NoData header="No Triggers Available" />
          )}
        </div>
      </div>
    </div>
  );
};

export default Trigger;